import {
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  PlusCircleOutlined,
  ShareAltOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons"
import type { MenuItemType } from "antd/es/menu/interface"

import type { AlbumListItem } from "../types/album.types.ts"

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

interface AlbumMenuRights {
  userId: string
  editAllowed: boolean
  deleteAllowed: boolean
  deleteForeignAllowed: boolean
}

interface AlbumMenuCallbacks {
  onView: (album: AlbumListItem) => void
  // Rename and Share both open the same combined name/description/visibility
  // dialog (`EditAlbumModal`) — there is one endpoint and one form behind
  // both entry points, matching how the app already edits an album.
  onEdit: (album: AlbumListItem) => void
  onAddFiles: (album: AlbumListItem) => void
  onToggleFavorite: (album: AlbumListItem) => void
  onDelete: (album: AlbumListItem) => void
}

// Single source of truth for the album row/card ⋯ menu, so All Files, the
// Albums directory and every list/grid view stay in sync rather than each
// hand-rolling its own copy of the same five actions.
export function buildAlbumMenuItems(
  album: AlbumListItem,
  rights: AlbumMenuRights,
  callbacks: AlbumMenuCallbacks,
  isFavorite: boolean,
  t: TranslateFn
): MenuItemType[] {
  const canDelete =
    rights.deleteForeignAllowed ||
    (rights.deleteAllowed && rights.userId === album.created_by)

  const items: MenuItemType[] = [
    {
      key: "open",
      label: t("files.albums.actions.open"),
      icon: <FolderOpenOutlined />,
      onClick: () => callbacks.onView(album)
    }
  ]

  if (rights.editAllowed) {
    items.push(
      {
        key: "rename",
        label: t("files.albums.actions.rename"),
        icon: <EditOutlined />,
        onClick: () => callbacks.onEdit(album)
      },
      {
        key: "share",
        label: album.is_visible_to_customer
          ? t("files.customerVisibility.makeInternalOnly")
          : t("files.customerVisibility.shareWithCustomer"),
        icon: <ShareAltOutlined />,
        onClick: () => callbacks.onEdit(album)
      },
      {
        key: "addFiles",
        label: t("files.albums.actions.addFiles"),
        icon: <PlusCircleOutlined />,
        onClick: () => callbacks.onAddFiles(album)
      }
    )
  }

  items.push({
    key: "favorite",
    label: isFavorite
      ? t("files.albums.actions.unfavorite")
      : t("files.albums.actions.favorite"),
    icon: isFavorite ? <StarFilled /> : <StarOutlined />,
    onClick: () => callbacks.onToggleFavorite(album)
  })

  if (canDelete) {
    items.push({
      key: "delete",
      label: t("files.albums.actions.delete"),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => callbacks.onDelete(album)
    })
  }

  return items
}
